// D12 consent step (accepted, Andrei 2026-10-25): an agent reads a person's personal memory only
// if that person grants it: read only, per agent, revocable and logged; an owner or admin cannot
// grant it for them. This is the person's own control, on their account page and on My agent:
// their agents, "Let it read" / "Take it back" per agent, and the log of past grants and
// revokes. `crews/me/personal-memory` is the caller's own session only, so an owner or admin
// sees their own agents here and never anyone else's. When granting is not available (memory
// off, the agent not yet linked to its person in Recall, an older Recall), the row says why and
// the button stays off; taking a grant back is always offered.
import {
  Badge,
  Button,
  Notice,
  SettingsGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Api } from "../../api/client";
import { formatDateTime } from "../format";
import { LoadError, Loading } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { problemCode } from "../structure/parts";

export const personalMemoryKey = (co: string) => ["company", co, "crews", "personal-memory"];

export function PersonalMemoryConsent({
  api,
  co,
  title = "Your personal memory",
  id = "personal-memory",
}: {
  api: Api;
  co: string;
  title?: string;
  /** The section's anchor (unique on the page: the account page has one per company). */
  id?: string;
}) {
  const qc = useQueryClient();
  const state = useQuery({
    queryKey: personalMemoryKey(co),
    queryFn: () => api.client.crews.personalMemory({ params: { co } }),
    retry: false,
  });
  const set = useMutation({
    mutationFn: (v: { access: "read" | "none" }) =>
      api.client.crews.putPersonalMemory({ params: { co }, body: { access: v.access } }),
    onSettled: () => void qc.invalidateQueries({ queryKey: personalMemoryKey(co) }),
  });
  // Not a person's own session here (an agency operator, a key): nothing of theirs to show.
  const code = problemCode(state.error);
  if (code === "forbidden" || code === "not_found") return null;
  return (
    <SettingsGroup id={id} title={title} data-slot="personal-memory">
      <div className="flex flex-col gap-3 p-4 text-[13px]">
        <p className="text-muted-foreground">
          Your agents cannot read your personal memory unless you let them. Access is read only: an
          agent never writes to it. You choose per agent, you can take it back at any time, and
          every change is logged. Only you can change this; an owner or admin cannot do it for you.
        </p>
        {state.isPending ? (
          <Loading label="Loading your agents" rows={1} />
        ) : state.isError ? (
          <LoadError what="Your agents" error={state.error} />
        ) : (
          <>
            {state.data.agents.length === 0 ? (
              <p className="text-muted-foreground">You have no agent in this company yet.</p>
            ) : (
              <ul className="flex flex-col gap-2" data-slot="personal-memory-agents">
                {state.data.agents.map((a) => (
                  <li
                    key={a.membership_id}
                    data-agent={a.membership_id}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1"
                  >
                    <span className="font-medium">{a.name}</span>
                    {a.access === "read" ? (
                      <Badge variant="warning">Can read your personal memory</Badge>
                    ) : (
                      <Badge variant="neutral">Cannot read it</Badge>
                    )}
                    <span className="ml-auto">
                      {a.access === "read" ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={set.isPending}
                          onClick={() => set.mutate({ access: "none" })}
                        >
                          Take it back
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          disabled={!a.can_grant || set.isPending}
                          onClick={() => set.mutate({ access: "read" })}
                        >
                          Let it read
                        </Button>
                      )}
                    </span>
                    {!a.can_grant && a.access !== "read" && a.unavailable ? (
                      <p className="w-full text-xs text-muted-foreground" data-slot="unavailable">
                        {a.unavailable}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {set.isError ? (
              <Notice tone="danger" role="alert" title="That did not go through">
                {problemText(set.error, "Try again.")}
              </Notice>
            ) : null}
            <ConsentLog log={state.data.log} />
          </>
        )}
      </div>
    </SettingsGroup>
  );
}

function ConsentLog({
  log,
}: {
  log: { at: string; agent_name: string; access: "read" | "none" }[];
}) {
  if (log.length === 0)
    return <p className="text-xs text-muted-foreground">No grants or revokes yet.</p>;
  return (
    <Table data-slot="personal-memory-log">
      <TableHeader>
        <TableRow>
          <TableHead scope="col">When</TableHead>
          <TableHead scope="col">Agent</TableHead>
          <TableHead scope="col">Change</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {log.map((e) => (
          <TableRow key={`${e.at}:${e.agent_name}:${e.access}`}>
            <TableCell className="text-muted-foreground">{formatDateTime(e.at)}</TableCell>
            <TableCell>{e.agent_name}</TableCell>
            <TableCell>
              {e.access === "read" ? "Let it read (read only)" : "Took it back"}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
